/**
 * ░░░░░▄▀▀▀▄░░░░░░░░░░░░░░░░░
 * ▄███▀░◐░░░▌░░░░░░░░░░░░░░░░
 * ░░░░▌░░░░░▐░░░░░░░░░░░░░░░░
 * ░░░░▐░░░░░▐░░░░░░░░░░░░░░░░
 * ░░░░▌░░░░░▐▄▄░░░░░░░░░░░░░░
 * ░░░░▌░░░░▄▀▒▒▀▀▀▀▄░░░░░░░░░
 * ░░░▐░░░░▐▒▒▒▒▒▒▒▒▀▀▄░░░░░░░
 * ░░░▐░░░░▐▄▒▒▒▒▒▒▒▒▒▒▀▄░░░░░
 * ░░░░▀▄░░░░▀▄▒▒▒▒▒▒▒▒▒▒▀▄░░░
 * ░░░░░░▀▄▄▄▄▄█▄▄▄▄▄▄▄▄▄▄▄▀▄░
 * ░░░░░░░░░░░▌▌░▌▌░░░░░░░░░░░
 * ░░░░░░░░░░░▌▌░▌▌░░░░░░░░░░░
 * ░░░░░░░░░▄▄▌▌▄▌▌░░░░░░░░░░░
 *
 * MIGRATION NOTICE
 *
 * We are in the process of re-writing our components using function components
 * and React Hooks, rather than extending Component.
 *
 * We have found that hooks make our components better, and - once you get the
 * hang of them - produce fewer bugs and edge cases than conventional lifecycle
 * methods (like componentDidMount and getDerivedStateFromProps).
 *
 * However, hooks can’t be used from class components. Therefore, we are trying
 * to eliminate class components wherever we can.
 *
 * If you’re changing this file, please consider contributing to this effort by
 * changing this component to be a function that uses hooks, and then removing
 * this notice.
 */
import PropTypes from 'prop-types';
import React from 'react';
import { css, cx } from 'emotion';

import NavIcon from './NavListIcon';

const ListItemStyles = css`
  width: 100%;
`;

const NavListItem = ({
  aria,
  className,
  clickHandler,
  icon,
  imageUrl,
  label,
  panel,
  target,
  url,
}) => (
  <li className={cx(ListItemStyles, className)}>
    {imageUrl && <img src={imageUrl} alt={label} width="50px" />}
    {url ? (
      <a
        aria-current={aria}
        href={url}
        id={label.toLowerCase()}
        onClick={clickHandler}
        target={target}
      >
        {icon && <NavIcon iconType={icon} />}
        {label}
      </a>
    ) : (
      // TODO: Add keyboard handler
      <span>{label}</span>
    )}
    {panel}
  </li>
);

NavListItem.defaultProps = {
  aria: null,
  className: '',
  clickHandler: null,
  icon: '',
  imageUrl: '',
  panel: null,
  target: '_self',
  url: '',
};

NavListItem.propTypes = {
  /**
   * Aria current for list item
   */
  aria: PropTypes.string,
  /**
   * Class name to pass down styles for list item
   */
  className: PropTypes.string,
  /**
   * Function that is triggered on click. Often used
   * for tracking etc.
   */
  clickHandler: PropTypes.func,
  /**
   * Allows you to add an icon before a list item
   */
  icon: PropTypes.string,
  /**
   * Allows you to add an image before a list item
   */
  imageUrl: PropTypes.string,
  /**
   * The display text for the item
   */
  label: PropTypes.string.isRequired,
  /**
   * React component panel to toggle when you click on a list item.
   */
  panel: PropTypes.node,
  /**
   * href target, allowing you to force a new window etc.
   */
  target: PropTypes.string,
  /**
   * URL for the list item link
   */
  url: PropTypes.string,
};

export default NavListItem;
